import JsonDiff from '@/components/Diff/JsonDiff';
import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import { RunMessagesInline } from '@/components/RunMessagesInline';
import { filterOutputs } from '@/components/RunsTable/utils/filterInputs';
import { Button, Text, Tooltip } from '@/design-system';
import { DiffChangeType } from '@/types/diff';
import { cn } from '@/utils/tailwind';

import { CodeDataReadOnly, CodeText } from '../../components/Code/Code';
import {
  DatasetSchema,
  ExampleSchema,
  RunOutputSchema,
  RunSchema,
} from '../../types/schema';
import {
  getFlattenedArrayOfGenerations,
  isMessageLike,
  maybeGetArrayOfMessages,
} from '../../utils/messages';
import { ForeignDataTreeView } from '../Graph/src/components/debug/common/foreign-data-tree';
import {
  foreignDataToTree,
  parseForeignData,
} from '../Graph/src/data/foreign-data';
import { MAX_CELL_HEIGHT } from './DatasetSessionCompare.utils';
import { ExperimentLanguage } from './types';

export function DatasetSessionCompareOutputs(props: {
  outputs: unknown;
  outputsPreview?: string | undefined;
  diffs?: DiffChangeType[] | undefined;
  onOpenDetailSidebar?: () => void;
  error?: RunSchema['error'];
  inputs: ExampleSchema['inputs'] | RunSchema['inputs'] | string | undefined;
  dataType: DatasetSchema['data_type'];
  language: ExperimentLanguage;
  truncate: boolean;
  lineClamp?: number;
  metricsHidden?: boolean;
  displayAsCode?: boolean;
}) {
  const outputsOrPreview = props.outputs ?? props.outputsPreview;
  const filteredOutput = filterOutputs(
    outputsOrPreview,
    {},
    {
      showEmpty: true,
    }
  );

  const showError =
    !(filteredOutput && filteredOutput.length > 0) && !!props.error;

  const truncatedElement = props.truncate && (
    <div
      data-testid="truncatedOutputElement"
      className={cn(
        'pointer-events-auto line-clamp-5 max-h-[100px] overflow-hidden text-sm font-medium opacity-100',
        showError && 'text-error'
      )}
      style={{
        WebkitLineClamp: props.lineClamp,
        transition: 'max-height 0.2s ease, opacity 0.2s ease',
      }}
    >
      {showError && props.metricsHidden && (
        <Tooltip
          title={<div className="line-clamp-3">{props.error}</div>}
          tooltipClassName="max-w-[500px] overflow-hidden p-3"
        >
          <span className="mr-2 inline-block align-middle text-error">
            Error:
          </span>
        </Tooltip>
      )}
      <Text>
        {showError
          ? props.error
          : filteredOutput === undefined
          ? ''
          : typeof filteredOutput !== 'string'
          ? JSON.stringify(filteredOutput).slice(0, 500)
          : filteredOutput?.slice(0, 500)}
      </Text>
    </div>
  );

  const cnNoTruncation = cn(
    props.truncate
      ? 'max-h-[20px] min-h-[20px]'
      : 'pointer-events-auto grow-[100] opacity-100'
  );
  return (
    <div className="relative h-full w-full">
      {props.truncate ? (
        truncatedElement
      ) : (
        <div
          className={cn(
            'pointer-events-none relative w-full whitespace-normal break-words break-all opacity-0',
            typeof outputsOrPreview !== 'string' && 'overflow-auto',
            cnNoTruncation
          )}
          style={{
            maxHeight: !props.truncate ? MAX_CELL_HEIGHT : undefined,
          }}
        >
          <div className="flex flex-col items-start gap-2">
            {props.error && !props.truncate && (
              <ExpandableErrorAlert
                error={props.error}
                defaultOpen={!outputsOrPreview}
              />
            )}
            <OutputElement
              dataType={props.dataType}
              outputsOrPreview={outputsOrPreview}
              diffs={props.diffs}
              cnNoTruncation={cnNoTruncation}
              language={props.language}
              displayAsCode={!!props.displayAsCode}
            />
            {!props.truncate && !props.outputs && props.outputsPreview && (
              <Tooltip title="Truncated output, click to expand" side="bottom">
                <Button
                  variant="underlined"
                  color="secondary"
                  onClick={props.onOpenDetailSidebar}
                >
                  Expand {props.diffs ? `for full diff` : ''}
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const OutputElement = ({
  outputsOrPreview,
  diffs,
  dataType,
  cnNoTruncation,
  language,
  displayAsCode,
}: {
  outputsOrPreview: string | RunOutputSchema | undefined;
  diffs?: DiffChangeType[] | undefined;
  dataType: DatasetSchema['data_type'];
  cnNoTruncation: string;
  language: ExperimentLanguage;
  displayAsCode: boolean;
}) => {
  const defaultOutputElement = (
    <ForeignDataTreeView
      data={foreignDataToTree({
        data: parseForeignData(outputsOrPreview),
      })}
    />
  );

  if (
    !outputsOrPreview ||
    (typeof outputsOrPreview === 'object' &&
      Object.keys(outputsOrPreview).length === 0)
  ) {
    return <Text className="text-disabled">No output</Text>;
  }

  if (diffs) {
    return <JsonDiff diffs={diffs} truncate />;
  }

  if (dataType === 'kv') {
    const messages = isMessageLike(outputsOrPreview)
      ? [outputsOrPreview]
      : maybeGetArrayOfMessages(outputsOrPreview);
    if (messages) {
      return <RunMessagesInline messages={messages} omitMaxHeight />;
    }
  }

  if (displayAsCode && (language === 'json' || language === 'yaml')) {
    return (
      <CodeDataReadOnly
        className={cn('transition-all', cnNoTruncation)}
        language={language}
        value={outputsOrPreview}
        omitMaxHeight
      />
    );
  }

  if (displayAsCode) {
    return defaultOutputElement;
  }
  if (typeof outputsOrPreview === 'string') {
    return (
      <CodeText
        className={cnNoTruncation}
        value={outputsOrPreview}
        removePadding
      />
    );
  }
  const message = getFlattenedArrayOfGenerations(
    outputsOrPreview as RunOutputSchema
  )[0]?.['message'];
  // todo: chat type datasets are deprecated but as of writing this
  // the playground experiment table manually sets the data_type to chat
  // so we need to support it for now
  if (dataType === 'chat' && isMessageLike(message)) {
    const output =
      'output' in outputsOrPreview ? outputsOrPreview?.output : undefined;
    return (
      <RunMessagesInline
        messages={[message]}
        toolCalls={
          typeof output === 'object' && output && 'tool_calls' in output
            ? output.tool_calls
            : undefined
        }
        omitMaxHeight
      />
    );
  }
  if (dataType === 'chat') {
    const output =
      'output' in outputsOrPreview ? outputsOrPreview?.output : undefined;
    const outputMessage =
      typeof output === 'string'
        ? {
            type: 'ai',
            data: { content: output },
          }
        : output?.kwargs ?? output ?? outputsOrPreview;
    return (
      <RunMessagesInline
        messages={[outputMessage]}
        toolCalls={outputMessage?.tool_calls}
        omitMaxHeight
      />
    );
  }

  return defaultOutputElement;
};
